import {animate, state, style, transition, trigger} from '@angular/animations';
import {CommonModule} from '@angular/common';
import {Component, computed, inject, input, signal} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {LocalStorageKeys} from '@shared/utils/local-storage-keys.enum';
import {AngularSvgIconModule} from 'angular-svg-icon';
import {ClinicalRegistry} from 'src/domain/clinical-record/clinical-record';
import {ClinicalRecordType} from 'src/domain/enum/clinical-record-type.enum';
import {FileAreas} from 'src/domain/enum/file-areas.enum';
import {FilesService} from 'src/infrastructure/files/files.service';
import {getKeyFromValue} from 'src/shared/utils/utils';

@Component({
    selector: 'app-record-card',
    templateUrl: './record-card.component.html',
    styleUrls: ['./record-card.component.scss'],
    standalone: true,
    imports: [
        TranslateModule,
        AngularSvgIconModule,
        CommonModule
    ],
    animations: [
        trigger('recordHeightAnimation', [
            state('false', style({ height: '*'})),
            state('true', style({ height: '0px'})),
            transition('true <=> false', animate('300ms'))
        ])
    ]
})
export class RecordCardComponent
{
    public readonly record = input.required<ClinicalRegistry>();

    private readonly translate = inject(TranslateService);

    private readonly fileService = inject(FilesService);

    public collapsed = signal(true);

    public loadingFile = computed(() => this.fileService.loading() && this.fileService.currentFileId() === this.record().document.id);

    public getRecordType(): string
    {
        return this.translate.instant(`GENERAL.CLINICAL_REGISTRY_TYPE.${getKeyFromValue(ClinicalRecordType, this.record().type)}`);
    }

    public getDate(date: Date): string
    {
        const formattedDate = new Date(date).toLocaleString(
            this.translate.currentLang, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            }
        ).replace('.', '');

        return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    }

    public getTime(date: Date): string
    {
        return new Date(date).toLocaleString(
            this.translate.currentLang, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }
        );
    }

    public hasAnyAdditionalInformation(): boolean
    {
        return !!this.record()?.specialty ||
            !!this.record()?.clinician ||
            !!this.record()?.continuousMedication ||
            !!this.record()?.frequency ||
            !!this.record()?.examType;
    }

    public getCollapsedState(): string
    {
        return (!((!!this.record()?.description || !!this.record()?.document || this.hasAnyAdditionalInformation()) && !this.collapsed())).toString();
    }

    public openDocument(event: Event): void
    {

        this.fileService.getFile(
            {
                name: this.record().document.name,
                mimeType: this.record().document.mimeType
            },
            {
                id: this.record().document.id,
                area: FileAreas.CLINICAL_REGISTRY,
                qrCodeToken: localStorage.getItem(LocalStorageKeys.QR_CODE_TOKEN)
            }
        );

        event.stopPropagation();
    }
}
